import React from 'react';

interface ColorInputProps {
  label: string;
  value?: string | null;
  defaultValue: string;
  placeholder: string;
  hint?: string;
  /** Allow rgba()/rgb() in the text field (no hex-only pattern). */
  allowCssColor?: boolean;
  onChange: (value: string | null) => void;
}

/**
 * Extract the first hex color from a CSS value.
 * Handles gradients (returns first color) and solid colors (returns as-is).
 * Falls back to #000000 if no valid hex color is found.
 */
const getPreviewColor = (value: string): string => {
  const match = value.match(/#[0-9A-Fa-f]{6}/);
  return match ? match[0] : '#000000';
};

/**
 * Reusable color input component with both color picker and text input.
 * Shows a "Theme" indicator when displaying the theme default color
 * (i.e., when no custom value is set).
 */
export const ColorInput: React.FC<ColorInputProps> = ({
  label,
  value,
  defaultValue,
  placeholder,
  hint,
  allowCssColor,
  onChange,
}) => {
  const isUsingThemeDefault = !value;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}
      <div className="flex space-x-2">
        <div className="relative">
          <input
            type="color"
            value={getPreviewColor(value || defaultValue)}
            onChange={e => onChange(e.target.value)}
            className={`h-10 w-16 border rounded-md cursor-pointer ${
              isUsingThemeDefault ? 'border-dashed border-gray-400' : 'border-gray-300'
            }`}
          />
          {isUsingThemeDefault && (
            <span
              className="absolute -top-1 -right-1 bg-gray-600 text-white text-[9px] font-medium px-1 rounded"
              title="Using theme default color"
            >
              T
            </span>
          )}
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={e => {
            const v = e.target.value;
            if (v.trim() === '') onChange(null);
            else onChange(v);
          }}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder={isUsingThemeDefault ? `${placeholder} (theme)` : placeholder}
          {...(allowCssColor ? {} : { pattern: '^#[0-9A-Fa-f]{6}$' })}
        />
      </div>
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-1 text-xs text-blue-600 hover:text-blue-700"
        >
          Use theme default
        </button>
      )}
    </div>
  );
};

interface ColorFieldConfig {
  key: string;
  label: string;
  defaultValue: string;
  placeholder: string;
  hint?: string;
  allowCssColor?: boolean;
}

interface ColorCustomizationPanelProps {
  title?: string;
  description?: string;
  customColors?: Record<string, string | null | undefined>;
  colorFields: ColorFieldConfig[];
  onUpdateColor: (key: string, value: string | null) => void;
  /** @deprecated No longer used - the footer "Use Theme Colors" button handles resetting all colors */
  onClearAllColors?: () => void;
}

/**
 * Reusable panel for color customization with multiple color inputs.
 * Each color input has its own "Use theme default" link to reset individual colors.
 * The modal footer "Use Default Colors" button handles resetting all colors at once.
 * Colors marked with "T" are using the current theme/brand package default.
 */
export const ColorCustomizationPanel: React.FC<ColorCustomizationPanelProps> = ({
  title = 'Colors (Optional)',
  description = 'Customize colors. Colors marked with "T" use your theme defaults.',
  customColors,
  colorFields,
  onUpdateColor,
}) => (
  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
    {title ? <h3 className="font-medium text-gray-900">{title}</h3> : null}
    {description ? <p className="text-sm text-gray-600">{description}</p> : null}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {colorFields.map(field => (
        <ColorInput
          key={field.key}
          label={field.label}
          value={customColors?.[field.key]}
          defaultValue={field.defaultValue}
          placeholder={field.placeholder}
          hint={field.hint}
          allowCssColor={field.allowCssColor}
          onChange={value => onUpdateColor(field.key, value)}
        />
      ))}
    </div>
  </div>
);
